import { apiFetch } from "../api.js";
import type {
  CreateDecisionInput,
  Decision,
  DecisionStatus,
  DecisionsListResponse,
  UpdateDecisionInput,
} from "./types.js";

/**
 * Decisions API module — Phase 4 Session 2.
 *
 * Mostly append-only. PATCH only mutates `revisit_date`, `status`,
 * `superseded_by_decision_id`, `is_archived`. The historical
 * record (title/context/chosen_option/rationale) is immutable.
 */

export interface ListDecisionsArgs {
  status?: DecisionStatus;
  revisitWithinDays?: number;
  includeArchived?: boolean;
}

export function listDecisions(
  args: ListDecisionsArgs = {},
): Promise<DecisionsListResponse> {
  const params = new URLSearchParams();
  if (args.status) params.set("status", args.status);
  if (args.revisitWithinDays !== undefined) {
    params.set("revisit_within_days", String(args.revisitWithinDays));
  }
  if (args.includeArchived) params.set("include_archived", "true");
  const qs = params.toString();
  return apiFetch<DecisionsListResponse>(
    `/v1/decisions${qs ? `?${qs}` : ""}`,
  );
}

export function createDecision(input: CreateDecisionInput): Promise<Decision> {
  return apiFetch<Decision>("/v1/decisions", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getDecision(decisionId: string): Promise<Decision> {
  return apiFetch<Decision>(
    `/v1/decisions/${encodeURIComponent(decisionId)}`,
  );
}

export function updateDecision(
  decisionId: string,
  input: UpdateDecisionInput,
): Promise<Decision> {
  return apiFetch<Decision>(`/v1/decisions/${encodeURIComponent(decisionId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteDecision(decisionId: string): Promise<void> {
  return apiFetch<void>(`/v1/decisions/${encodeURIComponent(decisionId)}`, {
    method: "DELETE",
  });
}
